import React from "react";

import { useLocalization } from "@fluent/react";

import { formatReceiptDate } from "../../format";

import styles from "./ReceiptDetailsDrawer.module.scss";

import type { ReceiptDetail } from "../../hooks/useReceipt";

interface UnmatchedEmptyCardProps {
  receipt: ReceiptDetail;
}

const daysRemaining = (
  retryExpiresAt: string | null | undefined
): number | null => {
  if (!retryExpiresAt) {
    return null;
  }
  const expiry = new Date(retryExpiresAt);
  if (Number.isNaN(expiry.getTime())) {
    return null;
  }
  const now = new Date();
  const diffMs = expiry.getTime() - now.getTime();
  const days = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
  return days > 0 ? days : null;
};

const UnmatchedEmptyCard = ({ receipt }: UnmatchedEmptyCardProps) => {
  const { l10n } = useLocalization();

  const renderContent = () => {
    if (receipt.status === "rejected") {
      const rejectorName = receipt.rejected_by?.name ?? "";
      const rejectedAt = formatReceiptDate(receipt.rejected_at);
      return (
        <>
          <p className="fontWeight--semibold margin--bottom--xs">
            {l10n.getString("receipts-drawer-rejected-title")}
          </p>
          <p className="fontColor--secondary fontSize--s">
            {l10n.getString("receipts-drawer-rejected-body", {
              name: rejectorName,
              date: rejectedAt,
            })}
          </p>
        </>
      );
    }

    if (receipt.status === "orphaned") {
      return (
        <p className="fontColor--secondary fontSize--s">
          {l10n.getString("receipts-drawer-orphaned-body")}
        </p>
      );
    }

    // no_match
    const days = daysRemaining(receipt.retry_expires_at);
    return (
      <>
        <p className="fontWeight--semibold margin--bottom--xs">
          {l10n.getString("receipts-drawer-no-match-title")}
        </p>
        <p className="fontColor--secondary fontSize--s">
          {l10n.getString("receipts-drawer-no-match-body")}
        </p>
        {days !== null && (
          <p className="fontColor--secondary fontSize--s margin--top--xs">
            {l10n.getString("receipts-drawer-no-match-countdown", { days })}
          </p>
        )}
      </>
    );
  };

  return (
    <div
      className={`${styles.card} border--all rounded--all--m padding--all--m`}
      data-testid="unmatched-empty-card"
    >
      {renderContent()}
    </div>
  );
};

export default UnmatchedEmptyCard;
